import { ColumnDef } from "@tanstack/react-table";
import ConfirmationModal from "components/modals/confirmation-modal";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipWrapper } from "components/permissions/permissions";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as QueryKeys from "data";
import { ApiKey } from "data/models";
import { deleteApiKey } from "data/mutations-common";
import { Copy, Gem, Pencil, Trash } from "lucide-react";
import { premiumProducts } from "pages/developer/apps/api-keys-page";
import { useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { ApiKeyRow } from "./api-keys-table";

interface ApiKeyProduct {
  productName: string;
  productDisplayName: string;
}

const renderProductChips = (products: string[]) => {
  const extraProducts: string[] = [];

  return (
    <div className="flex items-center gap-2">
      {products.map((product: string, index: number) => {
        if (index < 2)
          return (
            <Badge key={product} variant="outline">
              {product}
            </Badge>
          );
        extraProducts.push(product);
        return null;
      })}
      {extraProducts.length > 0 && (
        <Badge variant="outline" title={extraProducts.join(", ")}>
          {"+" + extraProducts.length}
        </Badge>
      )}
    </div>
  );
};

export const useColumns = (identityId: string): ColumnDef<ApiKeyRow>[] => {
  const history = useHistory();

  const queryClient = useQueryClient();

  const apiKeyDeleteMutation = useMutation({
    mutationFn: (selectedKey: string) => deleteApiKey(selectedKey),
    onSuccess: (deletedApiKey) => {
      queryClient.setQueryData<ApiKey[]>(
        [QueryKeys.apiKeys, identityId],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (key: ApiKey) => key.name !== deletedApiKey.name,
          );
        },
      );

      setDeleteModalIsOpen(false);
    },
  });

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  return [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "creator", header: "Creator" },
    {
      accessorKey: "premium",
      header: "Premium",
      cell: ({ row }) => {
        if (
          row.original.products
            .map((x: ApiKeyProduct) => x.productName)
            .some((product: string) => premiumProducts.includes(product))
        ) {
          return (
            <Badge variant="outline" className="border-info text-info gap-1">
              <Gem />
              Premium
            </Badge>
          );
        }
      },
    },
    {
      id: "key",
      header: "Key",
      cell: ({ row }) => (
        <div className="flex h-full items-center gap-1">
          <span className="min-w-0 flex-1 overflow-hidden">*************</span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              navigator.clipboard.writeText(row.original.consumerKey);
            }}
          >
            <Copy />
          </Button>
        </div>
      ),
    },
    {
      accessorKey: "products",
      header: "Products",
      cell: ({ row }) => {
        return renderProductChips(
          row.original.products.map((x: ApiKeyProduct) => x.productDisplayName),
        );
      },
    },
    {
      accessorKey: "createdDate",
      header: "Create Date",
      cell: ({ row }) => formatTimestamp(row.original.createdDate),
    },
    {
      accessorKey: "expireDate",
      header: "Expiry Date",
      cell: ({ row }) => formatTimestamp(row.original.expireDate),
    },
    {
      accessorKey: "status",
      header: "Status",
      enableSorting: false,
      size: 200,
      cell: ({ row }) => (
        <StatusGroup
          status={row.original.status}
          expireDate={row.original.expireDate}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            <PermissionTooltipWrapper
              permissionKeys={[PermissionsKeys.appsUpdate]}
            >
              <Button
                variant="ghost"
                size="icon"
                className="text-info"
                onClick={() => {
                  try {
                    history.push(
                      generatePath(Routes.developerApiKeysEditKey, {
                        keyName: row.original.id,
                      }),
                      {
                        keyName: row.original.id,
                      },
                    );
                  } catch (er) {}
                }}
              >
                <Pencil />
              </Button>
            </PermissionTooltipWrapper>
            <PermissionTooltipWrapper
              permissionKeys={[PermissionsKeys.appsDelete]}
            >
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => {
                  setSelectedKeyId(row.original.id);

                  setDeleteModalIsOpen(true);
                }}
              >
                <Trash />
              </Button>
            </PermissionTooltipWrapper>
            <ConfirmationModal
              isLoading={apiKeyDeleteMutation.isPending}
              isError={apiKeyDeleteMutation.isError}
              open={deleteModalIsOpen}
              header={`Delete this API key?`}
              body={`Deleting "${selectedKeyId}" API Key will permanently remove it from your account. This action cannot be undone.`}
              leftButtonText={"CANCEL"}
              leftButtonColor="primary"
              rightButtonText={"DELETE API KEY"}
              rightButtonColor="error"
              handleLeftButton={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
              handleRightButton={() => {
                if (selectedKeyId) {
                  apiKeyDeleteMutation.mutate(selectedKeyId);
                }
              }}
              handleClose={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
            />
          </div>
        );
      },
    },
  ];
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "approved":
      return "border-success text-success";
    case "revoked":
      return "border-destructive text-destructive";
    default:
      return "border-info text-info";
  }
};

const StatusGroup = (props: { status: string; expireDate: number }) => {
  const expired =
    props.expireDate !== -1 && props.expireDate <= Math.round(Date.now());

  return (
    <div className="flex h-full items-center">
      {expired ? (
        <Badge
          variant="outline"
          className="border-destructive text-destructive"
        >
          Expired
        </Badge>
      ) : (
        <Badge variant="outline" className={statusBadgeClass(props.status)}>
          {toTitleCase(props.status)}
        </Badge>
      )}
    </div>
  );
};

export const columnsAdmin: ColumnDef<ApiKeyRow>[] = [
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "createdDate",
    header: "Create Date",
    cell: ({ row }) => formatTimestamp(row.original.createdDate),
  },
  {
    accessorKey: "expireDate",
    header: "Expiry Date",
    cell: ({ row }) => formatTimestamp(row.original.expireDate),
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    size: 200,
    cell: ({ row }) => (
      <StatusGroup
        status={row.original.status}
        expireDate={row.original.expireDate}
      />
    ),
  },
];
